import { css } from '@emotion/react';
import { t } from 'i18n';
import { DateTime } from 'luxon';

import { getFlightDate } from '../../../utils';
import { getDayDifference } from '../../flights';
import { theme } from '../../theme';

type BottomMargin = 'none' | 'xs' | 'sm' | 'md' | 'lg' | 'xl';

const styles = {
  flight: (bottomMargin: BottomMargin, isHorizontal: boolean) => css`
    display: flex;
    flex-direction: ${isHorizontal ? 'column' : 'row'};
    margin-bottom: ${bottomMargin === 'none' ? 0 : theme.spacing[bottomMargin]};
    font-size: ${theme.fontSize.sm};

    @media (max-width: ${theme.breakpoints.tablet}) {
      flex-direction: column;
      gap: ${theme.spacing.xs};
      margin-bottom: ${bottomMargin === 'none' ? 0 : theme.spacing[bottomMargin]};
    }
  `,
  date: (isHorizontal: boolean) => css`
    margin-right: ${isHorizontal ? theme.spacing.xs : theme.spacing.md};
  `,
  stops: css`
    margin-left: ${theme.spacing.xs};
    color: ${theme.colors.gray[2]};
  `,
  flightTimes: css`
    display: flex;
    flex-direction: row;

    @container (max-width: 305px) {
      flex-direction: column;
    }
  `,
  stopText: css`
    color: ${theme.colors.gray[2]};
    margin-left: ${theme.spacing.xs};

    @container (max-width: 305px) {
      margin-left: 0;
    }
  `,
  flexRow: css`
    display: flex;
    flex-direction: row;
  `,
};

type Props = {
  fromCityName: string;
  fromAirportCode: string;
  toCityName: string;
  toAirportCode: string;
  departureDateTimeLocal: string;
  arrivalDateTimeLocal: string;
  journeyDuration: string;
  amountOfSegments: number;
  boundDate: string | undefined;
  isHorizontal?: boolean;
  className?: string;
  bottomMargin?: BottomMargin;
};

export const FlightOverview = ({
  fromCityName,
  fromAirportCode,
  toAirportCode,
  toCityName,
  departureDateTimeLocal,
  arrivalDateTimeLocal,
  journeyDuration,
  amountOfSegments,
  boundDate,
  isHorizontal = false,
  className,
  bottomMargin = 'md',
}: Props) => {
  const amountOfStops = amountOfSegments - 1;
  const stopText = amountOfStops > 0 ? `${amountOfStops} ${t('common:stop')}` : `${t('common:direct')}`;
  const dayDifference = getDayDifference(departureDateTimeLocal, arrivalDateTimeLocal);
  const startTimeLocal = DateTime.fromISO(departureDateTimeLocal, { setZone: true }).toFormat('HH:mm');
  const endTimeLocal = DateTime.fromISO(arrivalDateTimeLocal, { setZone: true }).toFormat('HH:mm');

  return (
    <>
      <div css={styles.flight(bottomMargin, isHorizontal ?? undefined)}>
        <span>
          {fromCityName && fromAirportCode && (
            <>
              {fromCityName} <b style={{ fontWeight: theme.fontWeight.black }}>{fromAirportCode}</b>
            </>
          )}
          {' - '}
          {toCityName && toAirportCode && (
            <>
              {toCityName}{' '}
              <b style={{ fontWeight: theme.fontWeight.black, marginRight: theme.spacing.md }}>{toAirportCode}</b>
            </>
          )}
        </span>
        {!isHorizontal && <div css={styles.date(isHorizontal)}>{getFlightDate(boundDate ?? '')}</div>}
        <div css={styles.flightTimes}>
          <div css={styles.flexRow}>
            {isHorizontal && (
              <div css={styles.date(isHorizontal)} className={className}>
                {getFlightDate(boundDate ?? '')},
              </div>
            )}
          </div>
          <div css={styles.stopText}>
            {startTimeLocal}
            {' - '}
            {endTimeLocal}
            {dayDifference > 0 ? `+${dayDifference}` : ''}
            {` (${stopText}, ${journeyDuration})`}
          </div>
        </div>
      </div>
    </>
  );
};
